import _ from 'lodash';
import { TDict } from '../../types/utilityTypes';
import { getFrontendRuntimeContext } from '../libs/frontendRuntimeContext';
export interface IClientAssetResolver {
  setAssets(assetsBaseUri: string, assets: Object): void;
  resolve(path: string): string;
}

export class ClientAssetResolver implements IClientAssetResolver {
  private assets: TDict<string>;
  private assetsBaseUri: string;
  private initialized: boolean;

  constructor() {
    this.assets = {};
    this.initialized = false;
  }

  public setAssets(assetsBaseUri: string, assets: any) {
    this.initialized = true;
    this.assets = _.extend({}, assets);
    this.assetsBaseUri = assetsBaseUri;
  }

  public resolve(path: string) {
    if (!this.initialized) {
      throw new Error(`assetResolverService.rsolve called before initialization: ${path}`);
    }

    if (this.assets[path]) {
      return `${this.assetsBaseUri}assets/${this.assets[path]}`;
    }

    return '';
  }

  public relativePath(path: string, scaleOptions?: Partial<TAssetScaleOptions>) {
    if (!this.initialized) {
      throw new Error(`assetResolverService.relativePath called before initialization: ${path}`);
    }

    if (!path) {
      console.error('ClientAssetResolver.relativePath called with missing path', typeof path);
      console.trace();
      return '';
    }

    if (/^(https?:)?\/\//.test(path)) {
      return path;
    }

    const cleanPath = path.charAt(0) === '/' ? path.replace(/^\//g, '') : path;

    // Cloudflare can auto-resize images with path updates, so we do this
    // to improve delivery speed of assets without having to support multiple
    // uploaded sizes:
    //
    // https://developers.cloudflare.com/images/transform-images/transform-via-url/
    let cdnAutoResizePrefix = '';
    if (scaleOptions && this.assetsBaseUri.indexOf('.raiderio.net') !== -1) {
      // TODO: do not hardcode .raiderio.net

      cdnAutoResizePrefix = getPathPrefixForAutoScale(scaleOptions);
      if (cdnAutoResizePrefix) {
        cdnAutoResizePrefix = cdnAutoResizePrefix.substring(1);
      }
    }

    if (cleanPath.indexOf('wow/') === 0) {
      // this folder is forced to pull from the cdn
      return `${RIO_SITE.cdn_url}/${cdnAutoResizePrefix}${cleanPath}`;
    } else {
      return `${this.assetsBaseUri || ''}${cdnAutoResizePrefix}${cleanPath}`;
    }
  }
}

const resolver = new ClientAssetResolver();

const resolverBridge: IClientAssetResolver & Pick<ClientAssetResolver, 'relativePath'> = {
  setAssets(assetsBaseUri, assets) {
    const target = getFrontendRuntimeContext()?.assetResolver || resolver;
    target.setAssets(assetsBaseUri, assets);
  },
  resolve(path) {
    const target = getFrontendRuntimeContext()?.assetResolver || resolver;
    return target.resolve(path);
  },
  relativePath(path, scaleOptions) {
    const target = getFrontendRuntimeContext()?.assetResolver || resolver;
    return target.relativePath(path, scaleOptions);
  }
};

export type TAssetScaleOptions = {
  width: number;
  height: number;
  quality: number;

  // via: https://developers.cloudflare.com/images/optimization/features/#format--f
  format: 'auto' | 'avif' | 'webp' | 'jpeg' | 'baseline-jpeg' | 'json';
};

function getPathPrefixForAutoScale(scaleOptions: Partial<TAssetScaleOptions>) {
  const parts: string[] = [];
  if (scaleOptions.quality) {
    parts.push(`quality=${scaleOptions.quality}`);
  }
  if (scaleOptions.width) {
    parts.push(`width=${scaleOptions.width}`);
  }
  if (scaleOptions.height) {
    parts.push(`height=${scaleOptions.height}`);
  }
  if (scaleOptions.format) {
    parts.push(`format=${scaleOptions.format}`);
  }
  if (parts.length > 0) {
    return `/cdn-cgi/image/${parts.join(',')}/`;
  }
  return '';
}

// allows us to set auto-scaling CDN params on a URL generated off of a Cloudflare cdn
export function scaledAssetUrl(url: string | null | undefined, scaleOptions: Partial<TAssetScaleOptions>) {
  if (!url) {
    return '';
  }

  try {
    const parts = new URL(url);
    if (parts.hostname.endsWith('.raiderio.net')) {
      // TODO: do not hardcode .raiderio.net

      const autoScalePrefix = getPathPrefixForAutoScale(scaleOptions);
      if (autoScalePrefix) {
        parts.pathname = autoScalePrefix.substring(0, autoScalePrefix.length - 1) + parts.pathname;
      }

      return parts.toString();
    }
  } catch (err) {
    console.error('scaledAssetUrl encountered error', err);
  }

  return url;
}

export function asset(path: string, scaleOptions?: Partial<TAssetScaleOptions>) {
  return resolverBridge.relativePath(path, scaleOptions);
  // For testing
  // return window.location.hostname === 'localhost' ? `https://cdn.raiderio.net/${path}` : resolver.relativePath(path);
}

export function optionalAsset(path?: string | null) {
  return path ? asset(path) : '';
}

export default resolverBridge;
